import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Button,
  FieldLabel,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
  TextInput,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { detectCreditCard } from '@vend/credit-card-detector'
import { T, useT } from '@transifex/react'
import {
  PaymentReferencePaymentOption,
  SalePayment,
} from '../../sale/payment-panel/PaymentModal/types'
import { HasCCErrorMessage } from '../../shared/has-cc-error-message/HasCCErrorMessage'

type Props = {
  paymentOptions: PaymentReferencePaymentOption
  onSuccess: (result: SalePayment) => Promise<void>
  onClose: () => void
}

export const PaymentReferenceModal: React.FC<Props> = ({
  paymentOptions,
  onSuccess,
  onClose,
}) => {
  const t = useT()
  const [referenceNumber, setReferenceNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const trimmedReferenceNumber = referenceNumber.trim()
  const hasCreditCard = detectCreditCard(trimmedReferenceNumber)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      await onSuccess({
        paymentAmount: paymentOptions.amount,
        paymentType: paymentOptions.paymentType,
        paymentReference: trimmedReferenceNumber || undefined,
      })
    } catch {
      ToastNotifications.negative(
        t("Payment couldn't be saved. Try again.", {
          _context: 'Payment reference modal error',
        })
      )
      setIsSubmitting(false)
    }
  }

  const handleSkip = async () => {
    setIsSubmitting(true)
    try {
      await onSuccess({
        paymentAmount: paymentOptions.amount,
        paymentType: paymentOptions.paymentType,
      })
    } catch {
      ToastNotifications.negative(
        t("Payment couldn't be saved. Try again.", {
          _context: 'Payment reference modal error',
        })
      )
      setIsSubmitting(false)
    }
  }

  return createPortal(
    <Modal size="medium" onClose={onClose} dismissable={!isSubmitting}>
      <ModalHeader>
        <T
          _str="Add a reference number"
          _context="Payment reference modal header"
        />
      </ModalHeader>

      <ModalContent>
        <Text>
          <T
            _str="Add a reference number for this payment, such as a check number or invoice number."
            _context="Payment reference modal description"
          />
        </Text>
        <div className="vd-mt5">
          <FieldLabel
            label={t('Reference number', { _context: 'Label' })}
            supplementaryLabel={t('(optional)', { _context: 'Label' })}
          >
            <TextInput
              autoFocus
              placeholder={t('Enter a reference number', {
                _context: 'Placeholder',
              })}
              value={referenceNumber}
              onChange={e => setReferenceNumber(e.currentTarget.value)}
              disabled={isSubmitting}
              maxLength={36}
              hasError={hasCreditCard}
              data-testid="payment-reference-input"
            />
          </FieldLabel>
          {hasCreditCard && <HasCCErrorMessage />}
        </div>
      </ModalContent>

      <ModalActions className="vd-btn-group">
        <Button
          type="button"
          variant="supplementary"
          onClick={handleSkip}
          disabled={isSubmitting}
          data-testid="skip-payment-reference-btn"
        >
          <T _str="Skip" _context="Payment reference modal skip button" />
        </Button>
        <Button
          type="button"
          variant="go"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!trimmedReferenceNumber || hasCreditCard}
          data-track="complete-payment-with-reference"
          data-testid="complete-payment-reference-btn"
        >
          <T _str="Add reference" _context="Payment reference modal button" />
        </Button>
      </ModalActions>
    </Modal>,
    document.body
  )
}
